import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';
import Avatar from '../components/Avatar';
import BadgePreviewModal from '../components/BadgePreviewModal';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis } from 'recharts';

const statusLabels = {
  draft: 'مسودة', submitted: 'مُرسل', under_review: 'قيد المراجعة',
  approved: 'معتمد', rejected: 'مرفوض', printed: 'مطبوع'
};
const statusColors = ['#2563eb', '#ca8a04', '#16a34a', '#dc2626', '#6d28d9', '#9ca3af'];

const API_ORIGIN = () => client.defaults.baseURL.replace('/api', '');

const TABS = [
  { key: 'overview', label: 'نظرة عامة' },
  { key: 'design', label: 'تصميم البطاقة' },
  { key: 'back', label: 'ظهر البطاقة' },
  { key: 'template', label: 'قالب مخصص' },
  { key: 'participants', label: 'المشاركون' },
];

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = user.role === 'admin' || user.role === 'reviewer';

  const [tab, setTab] = useState('overview');
  const [event, setEvent] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [copyMsg, setCopyMsg] = useState('');
  const [designForm, setDesignForm] = useState(null);
  const [savingDesign, setSavingDesign] = useState(false);
  const [designMsg, setDesignMsg] = useState('');
  const [designError, setDesignError] = useState('');
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [sponsorsFile, setSponsorsFile] = useState(null);
  const [uploadingSponsors, setUploadingSponsors] = useState(false);
  const [templateFrontFile, setTemplateFrontFile] = useState(null);
  const [templateBackFile, setTemplateBackFile] = useState(null);
  const [uploadingTemplateFront, setUploadingTemplateFront] = useState(false);
  const [uploadingTemplateBack, setUploadingTemplateBack] = useState(false);
  const [showBadgePreview, setShowBadgePreview] = useState(false);

  const load = async () => {
    const { data } = await client.get(`/events/${id}`);
    setEvent(data);
    setDesignForm({
      description: data.description || '',
      badge_color: data.badge_color || '#0b8793',
      badge_secondary_color: data.badge_secondary_color || '#0f1626',
      badge_text_color: data.badge_text_color || '#ffffff',
      badge_title: data.badge_title || 'SPORTIX ACCREDITATION',
      badge_footer_text: data.badge_footer_text || '',
      badge_rules_text: data.badge_rules_text || '',
      badge_emergency_phone: data.badge_emergency_phone || '',
      public_registration_enabled: data.public_registration_enabled ?? 1
    });
    const p = await client.get('/participants', { params: { event_id: id, limit: 100 } });
    setParticipants(p.data.data);
    setTotal(p.data.total);
    client.get(`/dashboard/event/${id}/stats`).then(r => setStats(r.data)).catch(() => {});
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const publicLink = `${window.location.origin}/r/${id}`;
  const copyLink = () => {
    navigator.clipboard.writeText(publicLink);
    setCopyMsg('تم نسخ الرابط!');
    setTimeout(() => setCopyMsg(''), 2000);
  };

  const saveDesign = async (e) => {
    e.preventDefault();
    setSavingDesign(true);
    setDesignMsg(''); setDesignError('');
    try {
      await client.put(`/events/${id}`, designForm);
      setDesignMsg('تم حفظ التصميم بنجاح');
      load();
      setTimeout(() => setDesignMsg(''), 2500);
    } catch (err) {
      setDesignError(err.response?.data?.message || 'تعذر حفظ التصميم — تأكد من صلاحياتك');
    } finally {
      setSavingDesign(false);
    }
  };

  const uploadBanner = async () => {
    if (!bannerFile) return;
    setUploadingBanner(true);
    setDesignMsg(''); setDesignError('');
    try {
      const fd = new FormData();
      fd.append('banner', bannerFile);
      await client.post(`/events/${id}/banner`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setBannerFile(null);
      setBannerPreview(null);
      setDesignMsg('تم رفع البانر بنجاح');
      load();
      setTimeout(() => setDesignMsg(''), 2500);
    } catch (err) {
      setDesignError(err.response?.data?.message || 'تعذر رفع البانر — تأكد إن الصورة jpg/png/webp وأقل من 5MB');
    } finally {
      setUploadingBanner(false);
    }
  };

  const uploadSponsorsBanner = async () => {
    if (!sponsorsFile) return;
    setUploadingSponsors(true);
    setDesignMsg(''); setDesignError('');
    try {
      const fd = new FormData();
      fd.append('banner', sponsorsFile);
      await client.post(`/events/${id}/sponsors-banner`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSponsorsFile(null);
      setDesignMsg('تم رفع بانر الرعاة بنجاح');
      load();
      setTimeout(() => setDesignMsg(''), 2500);
    } catch (err) {
      setDesignError(err.response?.data?.message || 'تعذر رفع بانر الرعاة');
    } finally {
      setUploadingSponsors(false);
    }
  };

  const uploadTemplate = async (side) => {
    const file = side === 'front' ? templateFrontFile : templateBackFile;
    if (!file) return;
    side === 'front' ? setUploadingTemplateFront(true) : setUploadingTemplateBack(true);
    setDesignMsg(''); setDesignError('');
    try {
      const fd = new FormData();
      fd.append('template', file);
      await client.post(`/events/${id}/badge-template-${side}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      side === 'front' ? setTemplateFrontFile(null) : setTemplateBackFile(null);
      setDesignMsg(`تم رفع قالب ${side === 'front' ? 'الوجه' : 'الظهر'} بنجاح`);
      load();
      setTimeout(() => setDesignMsg(''), 2500);
    } catch (err) {
      setDesignError(err.response?.data?.message || 'تعذر رفع القالب');
    } finally {
      side === 'front' ? setUploadingTemplateFront(false) : setUploadingTemplateBack(false);
    }
  };

  const removeTemplate = async (side) => {
    if (!window.confirm(`إزالة القالب المخصص للـ${side === 'front' ? 'وجه' : 'ظهر'}؟ سيرجع التصميم الافتراضي.`)) return;
    await client.delete(`/events/${id}/badge-template/${side}`);
    load();
  };

  const exportParticipants = () => {
    window.open(`${client.defaults.baseURL}/participants/export?event_id=${id}&token=${localStorage.getItem('accred_token')}`, '_blank');
  };

  if (!event) return <div className="card">جارٍ التحميل...</div>;

  const bannerUrl = event.banner_path ? `${API_ORIGIN()}${event.banner_path}` : null;
  const statusData = stats?.byStatus.map(s => ({ name: statusLabels[s.status] || s.status, value: s.count })) || [];
  const visibleTabs = TABS.filter(t => (t.key === 'design' || t.key === 'back' || t.key === 'template') ? canManage : true);

  return (
    <div>
      <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 18 }}>
        <div>
          <button className="btn btn-outline" style={{ marginBottom: 10 }} onClick={() => navigate('/events')}>← كل الفعاليات</button>
          <h2 style={{ margin: 0 }}>{event.name_ar}</h2>
          <div style={{ color: 'var(--muted)', fontSize: 13 }}>{event.location} · {event.start_date} → {event.end_date}</div>
        </div>
        <button className="btn btn-primary" onClick={() => navigate(`/register?event=${id}`)}>+ تسجيل مشارك يدويًا</button>
      </div>

      <div className="no-print" style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--line)', marginBottom: 20, flexWrap: 'wrap' }}>
        {visibleTabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '10px 18px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13.5,
              fontWeight: tab === t.key ? 700 : 500, color: tab === t.key ? 'var(--signal-dark)' : 'var(--muted)',
              borderBottom: tab === t.key ? '2px solid var(--signal)' : '2px solid transparent', marginBottom: -1
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div>
          <div className="card" style={{ marginBottom: 20, borderTop: `4px solid ${event.badge_color || '#0b8793'}` }}>
            <h3 style={{ marginTop: 0 }}>رابط التسجيل العام</h3>
            <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: -6 }}>
              شارك هذا الرابط مع المشاركين ليسجّلوا أنفسهم مباشرة بدون الحاجة لحساب دخول.
            </p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <code className="mono card" style={{ padding: '8px 12px', flex: 1, minWidth: 260, background: '#fafbfc' }}>{publicLink}</code>
              <button className="btn btn-primary" onClick={copyLink}>نسخ الرابط</button>
              {copyMsg && <span style={{ color: 'var(--success)', fontSize: 13 }}>{copyMsg}</span>}
            </div>
          </div>

          {stats && (
            <div className="card">
              <h3 style={{ marginTop: 0 }}>تحليل هذه الفعالية</h3>
              <div className="grid grid-4" style={{ marginBottom: 16 }}>
                <div className="card stat-card"><div className="stat-num">{stats.total}</div><div className="stat-label">إجمالي المشاركين</div></div>
                {stats.byStatus.slice(0, 3).map(s => (
                  <div key={s.status} className={`card stat-card ${s.status === 'approved' ? 'seal' : ''}`}>
                    <div className="stat-num">{s.count}</div><div className="stat-label">{statusLabels[s.status]}</div>
                  </div>
                ))}
              </div>
              <div className="grid grid-2">
                <div>
                  <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 6 }}>توزيع الحالات</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={statusData} dataKey="value" nameKey="name" outerRadius={70} label>
                        {statusData.map((e, i) => <Cell key={i} fill={statusColors[i % statusColors.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 6 }}>حسب فئة الاعتماد</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={stats.byCategory}>
                      <XAxis dataKey="category" tick={{ fontSize: 10 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                        {stats.byCategory.map((e, i) => <Cell key={i} fill={e.color_code || '#1e3a8a'} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'design' && canManage && designForm && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <h3 style={{ margin: 0 }}>ألوان ونصوص البطاقة (الوجه)</h3>
            <button type="button" className="btn btn-outline" onClick={() => setShowBadgePreview(true)}>معاينة البطاقة (وجه + ظهر)</button>
          </div>
          {designMsg && <div className="alert alert-success" style={{ marginTop: 14 }}>{designMsg}</div>}
          {designError && <div className="alert alert-error" style={{ marginTop: 14 }}>{designError}</div>}

          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 14 }}>
            <form onSubmit={saveDesign} style={{ flex: 1, minWidth: 320 }}>
              <div className="form-grid">
                <div className="form-field">
                  <label>عنوان البطاقة</label>
                  <input value={designForm.badge_title} onChange={e => setDesignForm({ ...designForm, badge_title: e.target.value })} />
                </div>
                <div className="form-field">
                  <label>نص أسفل البطاقة (Footer)</label>
                  <input value={designForm.badge_footer_text} onChange={e => setDesignForm({ ...designForm, badge_footer_text: e.target.value })} placeholder="مثال: صالحة طوال أيام الفعالية فقط" />
                </div>
                <div className="form-field">
                  <label>اللون الأساسي (الهيدر)</label>
                  <input type="color" value={designForm.badge_color} onChange={e => setDesignForm({ ...designForm, badge_color: e.target.value })} />
                </div>
                <div className="form-field">
                  <label>اللون الثانوي (الفوتر)</label>
                  <input type="color" value={designForm.badge_secondary_color} onChange={e => setDesignForm({ ...designForm, badge_secondary_color: e.target.value })} />
                </div>
                <div className="form-field">
                  <label>لون النص على الهيدر</label>
                  <input type="color" value={designForm.badge_text_color} onChange={e => setDesignForm({ ...designForm, badge_text_color: e.target.value })} />
                </div>
                <div className="form-field">
                  <label>التسجيل العام</label>
                  <select value={designForm.public_registration_enabled} onChange={e => setDesignForm({ ...designForm, public_registration_enabled: Number(e.target.value) })}>
                    <option value={1}>مفعّل — رابط التسجيل يعمل</option>
                    <option value={0}>معطّل — الرابط مغلق</option>
                  </select>
                </div>
              </div>
              <div className="form-field" style={{ marginTop: 14 }}>
                <label>محتوى صفحة التسجيل العامة (وصف الفعالية للمشاركين)</label>
                <textarea rows="3" value={designForm.description} onChange={e => setDesignForm({ ...designForm, description: e.target.value })} />
              </div>
              <div className="form-field" style={{ marginTop: 14 }}>
                <label>بانر/شعار البطاقة (يظهر خلفية الهيدر في الوجه)</label>
                <input type="file" accept="image/*" onChange={e => {
                  const f = e.target.files[0];
                  setBannerFile(f);
                  setBannerPreview(f ? URL.createObjectURL(f) : null);
                }} />
                {bannerFile && (
                  <button type="button" className="btn btn-outline" style={{ marginTop: 8 }} onClick={uploadBanner} disabled={uploadingBanner}>
                    {uploadingBanner ? 'جارٍ الرفع...' : 'رفع البانر'}
                  </button>
                )}
              </div>
              <button className="btn btn-primary" style={{ marginTop: 18 }} disabled={savingDesign}>
                {savingDesign ? 'جارٍ الحفظ...' : 'حفظ التصميم والمحتوى'}
              </button>
            </form>

            <div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8, textAlign: 'center' }}>معاينة سريعة (تقريبية)</div>
              <div className="badge-preview">
                <div
                  className="bp-header"
                  style={{
                    background: bannerPreview || bannerUrl ? `linear-gradient(${designForm.badge_color}99, ${designForm.badge_color}99), url(${bannerPreview || bannerUrl})` : designForm.badge_color,
                    color: designForm.badge_text_color
                  }}
                >
                  <div className="bp-title">{designForm.badge_title}</div>
                  <div className="bp-sub">{event.name_ar}</div>
                </div>
                <div className="bp-body">
                  <div className="bp-photo" />
                  <div className="bp-name">اسم المشارك</div>
                  <span className="bp-cat" style={{ background: '#1e3a8a' }}>الفئة</span>
                </div>
                <div className="bp-footer" style={{ background: designForm.badge_secondary_color, color: designForm.badge_text_color }}>
                  {designForm.badge_footer_text || 'No. ACC-0000'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'back' && canManage && designForm && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>محتوى ظهر البطاقة</h3>
          {designMsg && <div className="alert alert-success">{designMsg}</div>}
          {designError && <div className="alert alert-error">{designError}</div>}
          <form onSubmit={saveDesign}>
            <div className="form-field">
              <label>قواعد الدخول (سطر لكل قاعدة — اتركه فارغًا للنص الافتراضي)</label>
              <textarea rows="5" value={designForm.badge_rules_text} onChange={e => setDesignForm({ ...designForm, badge_rules_text: e.target.value })}
                placeholder={'يجب ارتداء البطاقة طوال الوقت\nالدخول مقتصر على المناطق المصرح بها فقط\n...'} />
            </div>
            <div className="form-field" style={{ marginTop: 12 }}>
              <label>رقم الطوارئ (يظهر بشكل بارز في الخلف)</label>
              <input value={designForm.badge_emergency_phone} onChange={e => setDesignForm({ ...designForm, badge_emergency_phone: e.target.value })} placeholder="+971-4-2626271" />
            </div>
            <p style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 10 }}>
              دليل فئات الاعتماد (المربعات الملوّنة بالحروف) بيتولّد تلقائيًا من صفحة "فئات الاعتماد" — أي فئة تضيفها هناك تظهر هنا تلقائيًا.
            </p>
            <div className="form-field" style={{ marginTop: 14 }}>
              <label>بانر الرعاة (صورة واحدة عريضة تجمع كل الشعارات)</label>
              <input type="file" accept="image/*" onChange={e => setSponsorsFile(e.target.files[0])} />
              {sponsorsFile && (
                <button type="button" className="btn btn-outline" style={{ marginTop: 8 }} onClick={uploadSponsorsBanner} disabled={uploadingSponsors}>
                  {uploadingSponsors ? 'جارٍ الرفع...' : 'رفع بانر الرعاة'}
                </button>
              )}
              {event.sponsors_banner_path && !sponsorsFile && (
                <div style={{ fontSize: 11.5, color: 'var(--success)', marginTop: 6 }}>✓ يوجد بانر رعاة مرفوع بالفعل</div>
              )}
            </div>
            <button className="btn btn-primary" style={{ marginTop: 18 }} disabled={savingDesign}>
              {savingDesign ? 'جارٍ الحفظ...' : 'حفظ محتوى الظهر'}
            </button>
          </form>
        </div>
      )}

      {tab === 'template' && canManage && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap', gap: 10 }}>
            <h3 style={{ margin: 0 }}>قالب مصمَّم خارجيًا (اختياري — بديل كامل للتصميم التلقائي)</h3>
            <button type="button" className="btn btn-outline" onClick={() => setShowBadgePreview(true)}>معاينة البطاقة الآن (وجه + ظهر)</button>
          </div>
          <p style={{ fontSize: 12.5, color: 'var(--muted)' }}>
            صمّم البطاقة بنفسك في أي برنامج تصميم، وارفع النتيجة النهائية هنا (وجه وظهر منفصلين). النظام هيحط بيانات كل مشارك (الصورة، الاسم، QR...) فوق تصميمك تلقائيًا في نفس الأماكن المحددة في دليل القياسات.
          </p>
          {designMsg && <div className="alert alert-success">{designMsg}</div>}
          {designError && <div className="alert alert-error">{designError}</div>}
          <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
            <a href="/badge-template-guide-FRONT.pdf" target="_blank" rel="noreferrer" className="btn btn-outline">تنزيل دليل القياسات — الوجه</a>
            <a href="/badge-template-guide-BACK.pdf" target="_blank" rel="noreferrer" className="btn btn-outline">تنزيل دليل القياسات — الظهر</a>
          </div>

          <div className="grid grid-2">
            <div className="form-field">
              <label>قالب الوجه (صورة أو PDF)</label>
              <input type="file" accept="image/*,application/pdf" onChange={e => setTemplateFrontFile(e.target.files[0])} />
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                {templateFrontFile && (
                  <button type="button" className="btn btn-outline" onClick={() => uploadTemplate('front')} disabled={uploadingTemplateFront}>
                    {uploadingTemplateFront ? 'جارٍ الرفع...' : 'رفع قالب الوجه'}
                  </button>
                )}
                {event.badge_template_front_path && (
                  <button type="button" className="btn btn-danger" onClick={() => removeTemplate('front')}>إزالة القالب</button>
                )}
              </div>
              {event.badge_template_front_path && <div style={{ fontSize: 11.5, color: 'var(--success)', marginTop: 6 }}>✓ يوجد قالب وجه مخصّص مرفوع</div>}
            </div>

            <div className="form-field">
              <label>قالب الظهر (صورة أو PDF)</label>
              <input type="file" accept="image/*,application/pdf" onChange={e => setTemplateBackFile(e.target.files[0])} />
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                {templateBackFile && (
                  <button type="button" className="btn btn-outline" onClick={() => uploadTemplate('back')} disabled={uploadingTemplateBack}>
                    {uploadingTemplateBack ? 'جارٍ الرفع...' : 'رفع قالب الظهر'}
                  </button>
                )}
                {event.badge_template_back_path && (
                  <button type="button" className="btn btn-danger" onClick={() => removeTemplate('back')}>إزالة القالب</button>
                )}
              </div>
              {event.badge_template_back_path && <div style={{ fontSize: 11.5, color: 'var(--success)', marginTop: 6 }}>✓ يوجد قالب ظهر مخصّص مرفوع</div>}
            </div>
          </div>
        </div>
      )}

      {tab === 'participants' && (
        <div className="card">
          <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <h3 style={{ margin: 0 }}>المشاركون في هذه الفعالية ({total})</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-outline" onClick={exportParticipants}>تصدير Excel</button>
              <button className="btn btn-outline" onClick={() => window.print()}>طباعة</button>
            </div>
          </div>
          <table>
            <thead>
              <tr><th></th><th>الاسم</th><th>الفئة</th><th>الجهة</th><th>الحالة</th></tr>
            </thead>
            <tbody>
              {participants.map(p => (
                <tr key={p.id} className="no-print-hover" style={{ cursor: 'pointer' }} onClick={() => navigate(`/participants/${p.id}`)}>
                  <td><Avatar src={p.photo_path} name={p.full_name_ar} size={32} color={p.color_code} /></td>
                  <td style={{ color: 'var(--signal-dark)', fontWeight: 600 }}>{p.full_name_ar}</td>
                  <td><span className="badge" style={{ background: p.color_code }}>{p.category_name_ar}</span></td>
                  <td>{p.organization_name}</td>
                  <td><span className={`badge status-${p.status}`}>{statusLabels[p.status]}</span></td>
                </tr>
              ))}
              {participants.length === 0 && (
                <tr><td colSpan="5" style={{ textAlign: 'center', color: '#9ca3af' }}>لا يوجد مشاركون مسجلون بعد</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showBadgePreview && (
        <BadgePreviewModal
          previewUrl={`${client.defaults.baseURL}/badges/preview/event/${id}`}
          title={`معاينة بطاقة: ${event.name_ar}`}
          onClose={() => setShowBadgePreview(false)}
        />
      )}
    </div>
  );
}
